import type { Key } from 'react';
import { isArrayOf } from '../../common-functions';
import type { JsonData, JsonDataItem } from '../../generic-types';
import type { DynamicFormButtonSetting, DynamicFormField } from '../dynamic-form.interfaces';
import type { VisibilityConditionBase } from './visibility-condition-base';
import type { FieldSettingType, VisibilityCondition } from './visibility-condition.interfaces';

export class FieldSetting {
  private _hasSettingChanged = false;

  public get hasSettingChanged() {
    return this._hasSettingChanged;
  }

  public get isButtonSetting(): boolean {
    return this.isFormButton(this.fieldSetting);
  }

  constructor(
    private fieldSetting: FieldSettingType,
    private conditionGroups: Array<Array<VisibilityConditionBase>>,
    private fieldSettingsFlatMap?: Map<Key, DynamicFormField>
  ) {
    //  targetValueId array as strings instead of numbers.
    const conditions = conditionGroups.reduce((conditionArray, conditions) => {
      conditionArray.push(...conditions);
      return conditionArray;
    }, []);
    const entityIdConditions = conditions.filter(
      (condition) =>
        condition.fieldCondition.targetValueType === 'ENTITY_ID' &&
        isArrayOf(condition.fieldCondition.targetValueId, 'string')
    );
    if (entityIdConditions.length > 0) {
      for (let i = 0, length = entityIdConditions.length; i < length; i++) {
        const condition = entityIdConditions[i];
        const targetValueWritable = Object.getOwnPropertyDescriptor(
          condition.fieldCondition,
          'targetValueId'
        )?.writable;
        if (Array.isArray(condition.fieldCondition.targetValueId) && targetValueWritable === true) {
          const numberArray = condition.fieldCondition.targetValueId.map(Number);
          condition.fieldCondition.targetValueId = [...numberArray];
        }
      }
    }
  }

  /**
   * Runs the visibility condition groups to determine if a field should be visible.
   * @param {JsonDataItem} data - The updated data used to determine if the field should be visible.
   * @param {JsonData} groups - The user groups used to determine if the field should be visible.
   * @param {FieldSetting} parentFieldSetting - If the parent field setting is hidden then these conditions will not get run.
   */
  public setFieldVisibility(data: JsonDataItem, groups?: JsonData, parentFieldSetting?: FieldSetting) {
    let currentHiddenValue = true;
    if (!parentFieldSetting || !parentFieldSetting.fieldSetting.hidden) {
      for (let i = 0, length = this.conditionGroups.length; i < length; i++) {
        if (i === 0) {
          // the first group always runs
          currentHiddenValue = this.shouldBeHidden(this.conditionGroups[i], data, groups);
        } else {
          // each subsequent group runs only if the previous group was successful, if not it breaks out of the loop
          if (!currentHiddenValue) {
            currentHiddenValue = this.shouldBeHidden(this.conditionGroups[i], data, groups);
          } else {
            break;
          }
        }
      }
    }

    this._hasSettingChanged = this.fieldSetting.hidden !== currentHiddenValue;
    this.fieldSetting.hidden = currentHiddenValue;
  }

  /**
   * Runs a series of conditions to determine if the result would be to show the field or button.
   * @param {Array<VisibilityConditionBase>} conditions - The visibility conditions to evaluate.
   * @param {JsonDataItem} data - The data used to evaluate the conditions.
   * @param {JsonData} groups - The user groups used to evaluate the conditions.
   * @returns {boolean} Determines if the conditions would cause a field or button to be hidden or not.
   * @private
   */
  private shouldBeHidden(conditions: Array<VisibilityConditionBase>, data: JsonDataItem, groups?: JsonData): boolean {
    let isHidden = true;

    for (let i = 0, length = conditions.length; i < length; i++) {
      let _fieldFormSetting;

      if (
        this.fieldSettingsFlatMap &&
        conditions[i].sourceFieldId &&
        this.fieldSettingsFlatMap.has(conditions[i].sourceFieldId!)
      ) {
        _fieldFormSetting = this.fieldSettingsFlatMap.get(conditions[i].sourceFieldId!);
      }

      if (_fieldFormSetting && _fieldFormSetting.hidden === true) {
        // When the field is hidden because of access conditions, we still need to run the visibility conditions
        if (_fieldFormSetting.permissions?.read === false) {
          isHidden = !this.fieldSetting?.isVisibleForced;
        } else if (conditions.length === 1 || conditions[i].fieldCondition.conjugate === 'AND') {
          // the field we are checking is hidden, so we can skip any additional checks if there is no need to run the other conditions

          //force the field to be hidden, an earlier condition might have set it to visible
          isHidden = true;
          break;
        }
      } else {
        if (conditions[i].VisibilityType !== 'GROUP') {
          isHidden = conditions[i].runVisibilityCondition(data);
        } else {
          if (groups) {
            isHidden = conditions[i].runVisibilityCondition(groups);
          } else {
            isHidden = true;
          }
        }

        if (!this.runNextCondition(isHidden, conditions[i].fieldCondition)) {
          break;
        }
      }
    }

    return isHidden;
  }

  /**
   * Determines if the next visibility condition should be run. The purpose of visibility condition(s) is to set a field
   * to visible, with that understanding here is how isHidden in evaluated.
   *
   * 1. If the current isHidden value is true and the conjugate is AND then there is no reason to run the other
   * conditions because all conditions must be met for the field to be set visible.
   *
   * 2. If the current isHidden value is false and the conjugate is OR then there is no reason to run the other
   * conditions because only one of them must be met for the field to be set visible.
   *
   * @param {boolean} currentHiddenValue - The current hidden field value.
   * @param {VisibilityCondition} condition - The current condition to check.
   * @returns {boolean} Determines if the next visibility condition should be run.
   * @private
   */
  private runNextCondition(currentHiddenValue: boolean, condition: VisibilityCondition): boolean {
    if (currentHiddenValue && condition.conjugate === 'AND') {
      return false;
    } else if (!currentHiddenValue && condition.conjugate === 'OR') {
      return false;
    }

    return true;
  }

  private isFormButton(type: FieldSettingType) {
    return (type as DynamicFormButtonSetting).name !== undefined;
  }
}
